import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import Card from '../../components/UI/Card';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import { Users, DollarSign, Clock, FileText, ArrowLeft } from 'lucide-react';
import { useSettings } from '../../contexts/SettingsContext';
import { formatDateTime } from '../../utils/formatDateTime';

interface Customer {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  company?: string;
  contactPerson?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Project {
  _id: string;
  name: string;
  description?: string;
  customerName: string;
  startDate: string;
  endDate?: string;
  status: 'planning' | 'active' | 'on-hold' | 'completed' | 'cancelled';
  actualCost: number;
  teamMembers: Array<{
    user: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
    role: string;
  }>;
  location?: string;
  notes?: string;
  invoices: Array<{
    _id: string;
    invoiceNumber: string;
    totalAmount: number;
    status: string;
  }>;
  createdAt: string;
  updatedAt: string;
  workOrders?: Array<{
    _id: string;
    number: string;
    date: string;
  }>;
}

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { settings } = useSettings();
  const currencySymbol = settings?.invoice?.defaultCurrencySymbol || '৳';

  useEffect(() => {
    const fetchProjectAndCustomer = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await apiService.get(`/projects/${id}`);
        setProject(res.data);
        // Fetch all customers and find the one matching project.customerName
        const customersRes = await apiService.get('/customers');
        const customers: Customer[] = customersRes.data.customers || [];
        const found = customers.find(c => c.name === res.data.customerName);
        setCustomer(found || null);
      } catch (err) {
        setError('Failed to load project details.');
      } finally {
        setLoading(false);
      }
    };
    fetchProjectAndCustomer();
  }, [id]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>;
  }
  if (error) {
    return <div className="text-center text-red-600 p-8">{error}</div>;
  }
  if (!project) {
    return <div className="text-center text-gray-500 p-8">Project not found.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto py-10 px-4">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center px-3 py-2 mb-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg shadow hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        title="Back"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back
      </button>
      <Card className="dark:bg-gray-900">
        <div className="mb-6">
          <h2 className="text-3xl font-bold mb-2 text-black dark:text-white">{project.name}</h2>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 mb-2">
            Status: {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
          </span>
          <p className="text-gray-600 dark:text-gray-300 mb-2">{project.description}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <div className="flex items-center mb-2 text-gray-700 dark:text-gray-200"><Clock className="w-4 h-4 mr-2" />Start Date:</div>
            <div className="font-medium text-black dark:text-white">{formatDateTime(project.startDate, settings)}</div>
          </div>
          <div>
            <div className="flex items-center mb-2 text-gray-700 dark:text-gray-200"><Clock className="w-4 h-4 mr-2" />End Date:</div>
            <div className="font-medium text-black dark:text-white">{project.endDate ? formatDateTime(project.endDate, settings) : 'TBD'}</div>
          </div>
          <div>
            <div className="flex items-center mb-2 text-gray-700 dark:text-gray-200"><Users className="w-4 h-4 mr-2" />Customer Name:</div>
            <div className="font-medium text-black dark:text-white">{customer?.name || project.customerName || 'N/A'}</div>
          </div>
          <div>
            <div className="flex items-center mb-2 text-gray-700 dark:text-gray-200"><Users className="w-4 h-4 mr-2" />Customer Email:</div>
            <div className="font-medium text-black dark:text-white">{customer?.email || 'N/A'}</div>
          </div>
          <div>
            <div className="flex items-center mb-2 text-gray-700 dark:text-gray-200"><Users className="w-4 h-4 mr-2" />Customer Phone:</div>
            <div className="font-medium text-black dark:text-white">{customer?.phone || 'N/A'}</div>
          </div>
          <div>
            <div className="flex items-center mb-2 text-gray-700 dark:text-gray-200"><Users className="w-4 h-4 mr-2" />Customer Address:</div>
            <div className="font-medium text-black dark:text-white">{customer?.address || 'N/A'}</div>
          </div>
        </div>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-black dark:text-white mb-2">Team Members</h3>
          <div className="flex flex-wrap gap-2">
            {(project.teamMembers || []).length === 0 && <span className="text-gray-500 dark:text-gray-300">No team members.</span>}
            {(project.teamMembers || []).map((member) => (
              <span key={member.user._id} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                {member.user.firstName} {member.user.lastName} <span className="ml-2 text-gray-500 dark:text-gray-300">({member.role})</span>
              </span>
            ))}
          </div>
        </div>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-black dark:text-white mb-2">Invoices</h3>
          <DoubleScrollbar>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="px-4 py-2 text-left">Invoice #</th>
                  <th className="px-4 py-2 text-left">Amount</th>
                  <th className="px-4 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {(project.invoices || []).length === 0 && (
                  <tr><td colSpan={3} className="text-center text-gray-500 py-2">No invoices.</td></tr>
                )}
                {(project.invoices || []).map((inv) => (
                  <tr key={inv._id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900 transition-colors cursor-pointer">
                    <td className="px-4 py-2">
                      <Link to={`/invoices/${inv._id}`} className="text-blue-600 underline">
                        {inv.invoiceNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      {currencySymbol}{inv.totalAmount?.toLocaleString() ?? 'N/A'}
                    </td>
                    <td className="px-4 py-2">{inv.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DoubleScrollbar>
        </div>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-black dark:text-white mb-2">Work Orders</h3>
          {project.workOrders && project.workOrders.length > 0 ? (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="px-4 py-2 text-left">Work Order Number</th>
                  <th className="px-4 py-2 text-left">Work Order Date</th>
                </tr>
              </thead>
              <tbody>
                {project.workOrders.map((wo, idx) => (
                  <tr key={idx} className="border-b border-gray-200 dark:border-gray-700">
                    <td className="px-4 py-2">{wo.number}</td>
                    <td className="px-4 py-2">{wo.date ? formatDateTime(wo.date, settings) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-gray-500">No work orders.</div>
          )}
        </div>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-black dark:text-white mb-2">Other Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="font-semibold text-white">Location:</span> <span className="text-black dark:text-white">{
                typeof project.location === 'string'
                  ? project.location
                  : project.location && typeof project.location === 'object'
                    ? ((loc => [loc.address, loc.city, loc.state, loc.zipCode].filter(Boolean).join(', '))(project.location as any))
                    : 'N/A'
              }</span>
            </div>
            <div><span className="font-semibold text-white">Notes:</span> <span className="text-black dark:text-white">{project.notes || 'N/A'}</span></div>
            <div><span className="font-semibold text-white">Created At:</span> <span className="text-black dark:text-white">{formatDateTime(project.createdAt, settings)}</span></div>
            <div><span className="font-semibold text-white">Updated At:</span> <span className="text-black dark:text-white">{formatDateTime(project.updatedAt, settings)}</span></div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProjectDetailPage; 